import { useEffect, useState } from "react";
import styles from "./VideoListModal.module.css";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { setCurrentModule } from "../../../store/reducers/progressionReducer";
import { useDispatch, useSelector } from "react-redux";
import MainVideoModal from "../MainVideoModal/MainVideoModal";
import TallPanel from "../../SvgComponents/TallPanel";
import ButtonClose from "../../Componenents/ButtonClose/ButtonClose";
import { logEvent } from "../../../analytics";

export default function VideoListModal({ title, videos, categories }) {
  const dispatch = useDispatch();
  const { contextSafe } = useGSAP();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const currentModule = useSelector((state) => state.progression.currentModule);
  const [isViewingCategories, setIsViewingCategories] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    // Si une seule catégorie existe, la sélectionner automatiquement et passer directement à la vue des vidéos
    const categoryNames = Object.keys(categories);
    if (categoryNames.length === 1) {
      setSelectedCategory(categoryNames[0]);
      setIsViewingCategories(false); // Passer directement à la vue des vidéos
    }
  }, [categories]);

  // Réinitialiser l'état de chargement vidéo quand on change de vidéo
  useEffect(() => {
    setVideoLoaded(false);
    setVideoError(false);
  }, [currentIdx]);

  const handleClose = () => {
    dispatch(setCurrentModule("none"));
  };

  // Modifiez la fonction de clic sur une catégorie pour cacher la liste des catégories
  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
    setIsViewingCategories(false); // Masquer la liste des catégories
    setCurrentIdx(0); // Reset à la première vidéo

    // Tracker l'événement de sélection de catégorie
    logEvent("VideoList", `Video - ${categoryName}` );
  };

  // Ajoutez un bouton pour revenir à la liste des catégories
  const handleBackToCategories = () => {
    setIsViewingCategories(true);
    setSelectedCategory(null);

    // Tracker l'événement de retour aux catégories
    logEvent("VideoList", "back_to_categories", title);
  };

  // Gestion du chargement de la vidéo
  const handleVideoLoad = () => {
    setVideoLoaded(true);
    setVideoError(false);

    // Tracker l'événement de chargement de vidéo
    if (currentVideo) {
      logEvent("VideoList", `Video - ${selectedCategory}`,"part" ,currentVideo.title);
    }
  };

  // Gestion des erreurs de vidéo
  const handleVideoError = (e) => {
    console.error("Erreur de chargement vidéo:", e);
    setVideoError(true);
    setVideoLoaded(false);

    // Tracker l'erreur de chargement de vidéo
    if (currentVideo) {
      logEvent("VideoList", "video_error", currentVideo.title);
    }
  };

  // Gestion de la possibilité de lecture de la vidéo
  const handleCanPlay = () => {
    setVideoLoaded(true);
    setVideoError(false);
  };

  // Si pas de vidéos, afficher un message d'erreur
  if (!videos || videos.length === 0) {
    return (
      <div className={styles.videoListModal}>
        <div className={styles.modalContainer}>
          <div className={styles.windowList}>
            <div className={styles.videoList}>
              <p>no video available</p>
            </div>
          </div>
          <button className={styles.close} onClick={handleClose}></button>
        </div>
      </div>
    );
  }

  // Filtrer les vidéos par catégorie
  const filteredVideos = selectedCategory
    ? videos.filter((video) => video.category === selectedCategory)
    : videos;

  // Obtenir la première vidéo à afficher
  const currentVideo = filteredVideos[currentIdx] || filteredVideos[0];

  return (
    <div className={styles.videoListModal}>
      <div className={styles.modalContainer}>
        {/* Vidéo principale - Layout 2 colonnes */}
        {currentVideo && (
          <div className={styles.vanillaVideo}>
            {videoError ? (
              <div style={{
                width: '100%',
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f0f0f0',
                borderRadius: '8px',
                color: '#666'
              }}>
                Erreur de chargement de la vidéo
              </div>
            ) : (
              <video
                key={currentVideo.video} // Force le rechargement quand la vidéo change
                src={currentVideo.video}
                controls
                preload="metadata"
                poster={currentVideo.thumb}
                onLoadedData={handleVideoLoad}
                onCanPlay={handleCanPlay}
                onError={handleVideoError}
                playsInline
                controlsList="nodownload"
                style={{
                  pointerEvents: 'auto' // S'assurer que les contrôles sont cliquables
                }}
              />
            )}

            <div className={styles.videoInfos}>
              <p><strong>{currentVideo.title}</strong></p>
              <p>
                {currentVideo.author}</p>
              <p>{currentVideo.desc}</p>
              {!videoLoaded && !videoError && (
                <p style={{ color: '#666', fontSize: '0.8em' }}>Chargement de la vidéo...</p>
              )}
            </div>
          </div>
        )}

        {/* Liste des vidéos */}
        <div className={styles.windowList}>
          {/* En-tête de la catégorie */}
          <div className={styles.categoryHeader}>
            <div className={styles.categoryInfo}>
              <h3>
                {isViewingCategories
                  ? title
                  : selectedCategory || title
                }
              </h3>
              <p>
                {isViewingCategories
                  ? `${Object.keys(categories).length} categories available`
                  : `${filteredVideos.length} videos available`
                }
              </p>
            </div>
            <div className={styles.liveStats}>
              <span className={styles.liveBadge}>New</span>
              <span className={styles.viewerCount}>
                viewers coming soon
              </span>
            </div>
          </div>

          {/* Contenu scrollable */}
          <div className={styles.videoList}>
            {isViewingCategories ? (
              // Vue des catégories
              <div className={styles.categoriesContainer}>
                {Object.keys(categories).map((categoryName) => (
                  <button
                    key={categoryName}
                    className={styles.categoryButton}
                    onClick={() => handleCategoryClick(categoryName)}
                  >
                    <h3>{categoryName}</h3>
                    <p>{categories[categoryName].description}</p>
                  </button>
                ))}
              </div>
            ) : (
              // Vue des vidéos de la catégorie sélectionnée
              <div className={styles.selectedCategoryView}>
                {/* Bouton de retour aux catégories (seulement si plus d'une catégorie) */}
                {Object.keys(categories).length > 1 && (
                  <button
                    className={styles.backButton}
                    onClick={handleBackToCategories}
                  >
                    ← Back to categories
                  </button>
                )}

                {/* Liste des vidéos de la catégorie sélectionnée */}
                <div className={styles.videosList}>
                  {filteredVideos.map((elt, i) => (
                    <div key={i} className={styles.videoContainer}>
                      <button
                        className={`${styles.videoThumb} ${selectedCategory === "Glossary" ? styles.glossaryThumb : ""
                          } ${i === currentIdx ? styles.active : ""
                          }`}
                        onClick={() => {
                          logEvent(selectedCategory || "all", "click", elt.title);
                          setCurrentIdx(i);
                        }}
                      >
                        {elt.thumb && <img src={elt.thumb} alt={elt.alt || elt.title} />}
                        <div className={styles.infos}>
                          <p>{elt.title}</p>
                          <p>{elt?.author || (elt?.duration || null)}</p>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bouton de fermeture */}
        <button className={styles.close} onClick={handleClose}></button>
      </div>
    </div>
  );
}
